import IdeyaModal from '../../../common/antd/ideya-modal.tsx'
import React, { useState } from 'react'
import IdeyaTypography from '../../../common/antd/ideya-typography.tsx'
import { Trans, useTranslation } from 'react-i18next'
import { selectThemeStyle } from '../../../features/setting/setting.slice.ts'
import { useAppSelector } from '../../../redux/hooks.ts'
import Col from 'antd/es/grid/col'
import Checkbox from 'antd/es/checkbox'
import Row from 'antd/es/grid/row'
import { convertPxToNumber } from '../../../utils/helpers.ts'
import { IdeyaButton } from '../../../common/antd/ideya-button/ideya-button.tsx'
import { setLocalStorageItem } from '../../../utils/local-storage.ts'
import i18n from '../../../i18n.ts'
import { useIsMobile } from '../../../common/hooks/use-is-mobile.ts'

interface NewFeatureModalProps {
  open: boolean
  closeModal: () => void
}

export const NewFeatureModal: React.FC<NewFeatureModalProps> = ({
  open,
  closeModal,
}) => {
  const themeStyle = useAppSelector(selectThemeStyle)
  const [dontShowAgain, setDontShowAgain] = useState<boolean>(false)
  const releaseNoteVersion = import.meta.env.VITE_RELEASE_NOTE_VERSION as string
  const { t } = useTranslation()
  const currentLanguage = i18n.resolvedLanguage ?? i18n.language ?? 'en'
  const isMobile = useIsMobile()

  const faqUrlMap: Record<string, string> = {
    en: 'https://asset.polymerideya.com/faq/en#Internal-liquidity-parameters',
    jp: 'https://asset.polymerideya.com/faq/jp#取引量の上限の特定割合',
    tw: 'https://asset.polymerideya.com/faq/zh-tw#特定比例成交量限制',
    cn: 'https://asset.polymerideya.com/faq/zh-cn#特定比例成交量限制',
  }

  const faqUrl = faqUrlMap[currentLanguage] ?? faqUrlMap.en

  const handleClose = () => {
    if (dontShowAgain) {
      setLocalStorageItem(releaseNoteVersion, { show: false })
    }

    closeModal()
  }

  return (
    <IdeyaModal
      open={open}
      isForm={false}
      title={t('release_note.new_feature_modal.title')}
      onCancel={handleClose}
      footer={
        <Row
          justify={isMobile ? 'start' : 'space-between'}
          align={isMobile ? 'stretch' : 'middle'}
          gutter={convertPxToNumber(themeStyle.marginXS)}
        >
          <Col xs={24} sm={24} md={12} lg={12} xl={12} style={{ display: 'flex', alignItems: 'center' }}>
            <Row
              gutter={convertPxToNumber(themeStyle.marginXS)}
              align={'middle'}
              style={{ width: '100%' }}
            >
              <Col>
                <Checkbox
                  checked={dontShowAgain}
                  onChange={(e) => setDontShowAgain(e.target.checked)}
                />
              </Col>
              <Col>
                <IdeyaTypography.Text>
                  {t('release_note.new_feature_modal.dont_show_again')}
                </IdeyaTypography.Text>
              </Col>
            </Row>
          </Col>
          <Col
            xs={24} sm={24} md={12} lg={12} xl={12}
            style={{
              display: 'flex',
              justifyContent: isMobile ? 'flex-start' : 'flex-end',
              marginTop: isMobile ? themeStyle.marginXS : 0,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: convertPxToNumber(themeStyle.marginXS),
                width: isMobile ? '100%' : 'auto',
              }}
            >
              <IdeyaButton ideyaType={'secondary'} onClick={handleClose}>
                {t('release_note.new_feature_modal.maybe_later')}
              </IdeyaButton>
              <IdeyaButton onClick={() => window.open(faqUrl, '_blank')}>
                {t('release_note.new_feature_modal.view_details')}
              </IdeyaButton>
            </div>
          </Col>
        </Row>
      }
    >
      <div style={{ marginBottom: themeStyle.marginBase }}>
        <IdeyaTypography.Text>
          <Trans
            i18nKey={'release_note.new_feature_modal.description'}
            components={{ bold: <b /> }}
          />
        </IdeyaTypography.Text>
        <ul
          style={{
            listStyleType: 'disc',
            paddingLeft: 20,
            marginTop: 8,
            marginBottom: 0,
          }}
        >
          <li>
            <IdeyaTypography.Text>
              <Trans
                i18nKey={'release_note.new_feature_modal.order_status'}
                components={{ italic: <i />, bold: <b /> }}
              />
            </IdeyaTypography.Text>
          </li>
          <li>
            <IdeyaTypography.Text>
              {t('release_note.new_feature_modal.fill_status')}
            </IdeyaTypography.Text>
          </li>
        </ul>
      </div>
      <img
        src={'https://asset.polymerideya.com/release-notes/2026-04-13.png'}
        alt={t('release_note.new_feature_modal.image_alt')}
      />
    </IdeyaModal>
  )
}
